<template>
  <div v-if="visible" class="refund-modal-overlay" @click="handleOverlayClick">
    <div class="refund-modal" @click.stop>
      <div class="refund-modal-header">
        <h3>退款选择</h3>
        <button class="close-btn" @click="handleCancel">×</button>
      </div>

      <div class="refund-modal-body">
        <div class="order-info">
          <div class="info-item">
            <span class="label">订单号：</span>
            <span class="value">{{ orderInfo.orderNo }}</span>
          </div>
          <div class="info-item">
            <span class="label">订单总额：</span>
            <span class="value">¥{{ orderInfo.amount.toFixed(2) }}</span>
          </div>
          <div class="info-item">
            <span class="label">退款金额：</span>
            <span class="value refund-amount">¥{{ totalRefundAmount.toFixed(2) }}</span>
          </div>
        </div>

        <!-- 商品选择列表 -->
        <div class="refund-items">
          <h4>选择退款商品</h4>
          <div class="items-list">
            <div v-for="item in orderInfo.items" :key="item.id" class="refund-item">
              <div class="item-info">
                <label class="checkbox-label">
                  <input
                    type="checkbox"
                    :checked="selectedItems.has(item.id)"
                    @change="toggleItem(item)"
                    :disabled="(item.remainingQuantity || item.quantity) <= 0 || item.itemStatus === 2"
                  />
                  <div class="item-details">
                    <span class="item-name">{{ item.name }}</span>
                    <span v-if="item.isGift" class="gift-tag">赠品</span>
                  </div>
                </label>
              </div>
              <div class="item-price-info">
                <span class="unit-price">¥{{ item.price.toFixed(2) }} × {{ item.quantity }}</span>
                <span class="subtotal">小计: ¥{{ item.subtotal.toFixed(2) }}</span>
              </div>
              <!-- 显示退款状态 -->
              <div v-if="item.refundedQuantity > 0" class="refund-status">
                <span class="refunded-info">已退 {{ item.refundedQuantity }}{{ item.unit || '件' }}</span>
                <span class="remaining-info">剩余可退 {{ item.remainingQuantity }}{{ item.unit || '件' }}</span>
              </div>
              <div class="refund-quantity" v-if="selectedItems.has(item.id)">
                <label>退款数量：</label>
                <input
                  type="number"
                  :value="getRefundQuantity(item)"
                  @input="updateRefundQuantity(item, $event)"
                  :max="item.remainingQuantity || item.quantity"
                  :min="1"
                  class="quantity-input"
                />
                <span class="max-hint">最多{{ item.remainingQuantity || item.quantity }}{{ item.unit || '件' }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="refund-reason">
          <label for="refundReason">退款原因：</label>
          <textarea
            id="refundReason"
            v-model="refundReason"
            placeholder="请输入退款原因..."
            rows="3"
            maxlength="200"
          ></textarea>
          <div class="char-count">{{ refundReason.length }}/200</div>
        </div>
      </div>

      <div class="refund-modal-footer">
        <button class="cancel-btn" @click="handleCancel">取消</button>
        <button class="confirm-btn" @click="handleConfirm" :disabled="loading || selectedItems.size === 0">
          {{ loading ? '处理中...' : `确认退款 (${selectedItems.size}件商品)` }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RefundModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    orderInfo: {
      type: Object,
      required: true
    }
  },
  emits: ['confirm', 'cancel'],
  data() {
    return {
      refundReason: '',
      loading: false,
      selectedItems: new Map(), // 存储选中的商品和退款数量 { item.id => { item, refundQuantity } }
    }
  },
  computed: {
    totalRefundAmount() {
      let total = 0
      this.selectedItems.forEach(({ item, refundQuantity }) => {
        total += (item.price * refundQuantity)
      })
      return total
    },
    refundData() {
      const refundItems = []
      const orderItemIds = []

      this.selectedItems.forEach(({ item, refundQuantity }) => {
        refundItems.push({
          order_item_id: item.id,
          refund_quantity: refundQuantity,
          refund_amount: item.price * refundQuantity
        })
        orderItemIds.push(item.id)
      })

      return {
        order_id: this.orderInfo.orderId,
        order_item_ids: orderItemIds,
        refund_items: refundItems,
        remark: this.refundReason.trim()
      }
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.refundReason = ''
        this.loading = false
        this.selectedItems.clear()
      }
    }
  },
  methods: {
    toggleItem(item) {
      if (this.selectedItems.has(item.id)) {
        this.selectedItems.delete(item.id)
      } else {
        // 默认退款数量为剩余可退数量
        const availableQuantity = item.remainingQuantity || item.quantity || 1
        this.selectedItems.set(item.id, {
          item,
          refundQuantity: availableQuantity
        })
      }
    },
    getRefundQuantity(item) {
      const selected = this.selectedItems.get(item.id)
      return selected ? selected.refundQuantity : 0
    },
    updateRefundQuantity(item, event) {
      const quantity = parseInt(event.target.value) || 0
      const maxQuantity = item.remainingQuantity || item.quantity
      if (quantity > 0 && quantity <= maxQuantity) {
        this.selectedItems.set(item.id, {
          item,
          refundQuantity: quantity
        })
      }
    },
    handleConfirm() {
      if (this.selectedItems.size === 0) {
        alert('请选择要退款的商品')
        return
      }
      this.$emit('confirm', this.refundData)
    },
    handleCancel() {
      this.$emit('cancel')
    },
    handleOverlayClick() {
      this.handleCancel()
    },
    setLoading(loading) {
      this.loading = loading
    }
  }
}
</script>

<style scoped>
.refund-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.refund-modal {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.refund-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
}

.refund-modal-header h3 {
  margin: 0;
  font-size: 18px;
  color: #262626;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #8c8c8c;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #f5f5f5;
  color: #595959;
}

.refund-modal-body {
  padding: 24px;
}

.order-info {
  background: #fafafa;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 20px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.info-item:last-child {
  margin-bottom: 0;
}

.info-item .label {
  color: #595959;
  font-size: 14px;
}

.info-item .value {
  font-weight: 500;
  color: #262626;
}

.info-item .value.amount {
  color: #ff4d4f;
  font-size: 16px;
  font-weight: 600;
}

.info-item .value.refund-amount {
  color: #ff4d4f;
  font-size: 18px;
  font-weight: bold;
}

/* 商品选择区域 */
.refund-items {
  margin-bottom: 20px;
}

.refund-items h4 {
  margin: 0 0 15px 0;
  color: #262626;
  font-size: 16px;
  font-weight: 600;
}

.items-list {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 10px;
}

.refund-item {
  border-bottom: 1px solid #f0f0f0;
  padding: 12px 0;
}

.refund-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.item-info {
  margin-bottom: 8px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  width: 100%;
}

.checkbox-label input[type="checkbox"] {
  margin-right: 12px;
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.item-details {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
}

.item-name {
  color: #262626;
  font-weight: 500;
}

.gift-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #f6ffed;
  color: #52c41a;
  border: 1px solid #b7eb8f;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 11px;
  margin-left: 6px;
  white-space: nowrap;
  line-height: 1;
  flex: 0 0 auto;
  min-width: 0;
}

.item-price-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-left: 28px;
  margin-bottom: 8px;
  font-size: 14px;
}

.unit-price {
  color: #666;
}

.subtotal {
  color: #1890ff;
  font-weight: 500;
}

.refund-quantity {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 28px;
  padding: 8px;
  background: #fafafa;
  border-radius: 4px;
}

.refund-quantity label {
  color: #595959;
  font-size: 14px;
  white-space: nowrap;
}

.quantity-input {
  width: 80px;
  padding: 4px 8px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  text-align: center;
}

.quantity-input:focus {
  outline: none;
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

.max-hint {
  color: #8c8c8c;
  font-size: 12px;
}

.refund-status {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-left: 28px;
  margin-bottom: 8px;
  padding: 6px 8px;
  background: #fff7e6;
  border-radius: 4px;
  font-size: 13px;
}

.refunded-info {
  color: #fa8c16;
  font-weight: 500;
}

.remaining-info {
  color: #1890ff;
}

.refund-reason {
  margin-bottom: 16px;
}

.refund-reason label {
  display: block;
  margin-bottom: 8px;
  color: #262626;
  font-weight: 500;
}

.refund-reason textarea {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 14px;
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;
}

.refund-reason textarea:focus {
  outline: none;
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

.char-count {
  text-align: right;
  font-size: 12px;
  color: #8c8c8c;
  margin-top: 4px;
}

.refund-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
}

.cancel-btn,
.confirm-btn {
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid;
}

.cancel-btn {
  background: white;
  color: #595959;
  border-color: #d9d9d9;
}

.cancel-btn:hover {
  border-color: #40a9ff;
  color: #40a9ff;
}

.confirm-btn {
  background: #1890ff;
  color: white;
  border-color: #1890ff;
  min-width: 80px;
}

.confirm-btn:hover:not(:disabled) {
  background: #40a9ff;
  border-color: #40a9ff;
}

.confirm-btn:disabled {
  background: #f5f5f5;
  color: #8c8c8c;
  border-color: #d9d9d9;
  cursor: not-allowed;
}
</style>